import * as React from "react";
import { Download, ExternalLink, FileText, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";

const DOWNLOAD_URL_REVOKE_DELAY_IN_MS = 100;

interface PdfCardProps {
  file: File;
  url: string;
  onDelete: () => void;
}

function formatSizeInMegabytes(sizeInBytes: number): string {
  return `${(sizeInBytes / 1024 / 1024).toFixed(2)} MB`;
}

export const PdfCard = ({ file, url, onDelete }: PdfCardProps) => {
  const { t } = useTranslation("common");

  const handleOpen = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    window.open(url, "_blank");
  };

  const handleDownload = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = file.name;
      link.style.display = "none";
      link.target = "_blank";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
      }, DOWNLOAD_URL_REVOKE_DELAY_IN_MS);
    } catch (error) {
      console.error("Download failed:", error);
      window.open(url, "_blank");
    }
  };

  return (
    <div className="rounded-md border border-border bg-white shadow-xs">
      <div className="flex items-start justify-between border-b border-border bg-slate-50 px-4 py-3">
        <div className="flex items-center space-x-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-red-100">
            <FileText className="h-5 w-5 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="wrap-break-word text-sm font-medium leading-tight text-slate-900">{file.name}</p>
            <p className="text-xs text-muted-foreground">{formatSizeInMegabytes(file.size)}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button size="xs" onClick={handleOpen}>
            <ExternalLink className="mr-1 h-3 w-3" />
            {t("editor.openPdf")}
          </Button>
          <Button variant="outline" size="xs" onClick={handleDownload}>
            <Download className="mr-1 h-3 w-3" />
            {t("editor.downloadPdf")}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
            className="text-muted-foreground hover:bg-muted hover:text-red-600"
            aria-label={t("editor.deletePdf")}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="p-4">
        <div className="relative h-96 w-full overflow-hidden rounded-md border border-border bg-slate-50">
          <object data={url} type="application/pdf" className="h-full w-full" title={`PDF Viewer - ${file.name}`}>
            <div className="flex h-full w-full flex-col items-center justify-center bg-slate-50 p-6">
              <FileText className="h-16 w-16 text-muted-foreground" />
              <p className="mt-4 text-lg font-medium text-slate-700">{t("editor.pdfPreview")}</p>
              <p className="mt-2 text-center text-sm text-muted-foreground">{t("editor.pdfPreviewHint")}</p>
              <div className="mt-4 flex items-center space-x-2 text-xs text-muted-foreground">
                <span>{t("editor.pdfFileLabel", { name: file.name })}</span>
                <span>•</span>
                <span>{formatSizeInMegabytes(file.size)}</span>
              </div>
            </div>
          </object>
        </div>
      </div>
    </div>
  );
};
